import { ReactNode, useCallback } from 'react';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { Colors } from '@shared/styles';
import { API } from '@shared/types/api';
import { formatRelativeTime } from '@shared/util/date';
import { i18n } from '@shared/util/i18n';
import { useNavigator } from '@shared/util/navigation';
import { MBBaseChatBanner } from './base';

interface MBChannelChatBannerProps {
  channel: API.ChannelChatThreadFragment;
  creator: API.ShortUserFragment;
}

export function MBChannelChatBanner({ channel, creator }: MBChannelChatBannerProps) {
  const { navigate } = useNavigator();

  const addPeople = useCallback(() => {
    navigate('ChannelAddPeople', { channelSlug: channel.slug });
  }, [navigate, channel.slug]);

  const editChannel = useCallback(() => {
    navigate('ChannelEdit', { channelSlug: channel.slug });
  }, [navigate, channel.slug]);

  const editChannelName = useCallback(() => {
    navigate('ChannelEdit', { channelSlug: channel.slug, autoFocus: 'name' });
  }, [navigate, channel.slug]);

  const editChannelDescription = useCallback(() => {
    navigate('ChannelEdit', { channelSlug: channel.slug, autoFocus: 'description' });
  }, [navigate, channel.slug]);

  return (
    <MBBaseChatBanner channel={channel} subtitle={channel.description}>
      <View>
        <Text>{i18n('chat.banner.doFromHere')}</Text>
        <ChannelDetailsLink onPress={addPeople}>
          {i18n('chat.banner.addTeamMembers')}
        </ChannelDetailsLink>
        <ChannelDetailsLink onPress={editChannelName}>
          {i18n('chat.banner.renameWorkspace')}
        </ChannelDetailsLink>
        <ChannelDetailsLink onPress={editChannelDescription}>
          {i18n('chat.banner.addDescription')}
        </ChannelDetailsLink>
        <ChannelDetailsLink onPress={editChannel}>
          {i18n('chat.banner.addImage')}
        </ChannelDetailsLink>
      </View>

      <View paddingTop={2}>
        <Text size="body2" color={Colors.v2.greenSecondary90}>
          {i18n('chat.banner.createdBy', {
            name: creator.fullName,
            date: formatRelativeTime(channel.createdAt),
          })}
        </Text>
      </View>
    </MBBaseChatBanner>
  );
}

function ChannelDetailsLink({ children, onPress }: { children: ReactNode; onPress: VoidFunction }) {
  return (
    <View row paddingTop={1}>
      <Text>
        {'• '}
        <Text weight="bold" onClick={onPress}>
          {children}
        </Text>
      </Text>
    </View>
  );
}
